// The X-ray shows through where the face's dots scatter, so it follows each wave's ring across
// the face and eases in and out with it. portrait-waves.ts eases the strength, and the face's
// context draws the X-ray at it.

import { FACE_BAND_WIDEN, RIPPLE_BAND_TAIL } from "@bfly/engine";
import { smoothstep } from "@bfly/engine/math";

// The fraction of the band's peak below which you can't see the scatter any more: a dot
// shrinks by less than 8% and moves less than 4px.
const SCATTER_SEEN = 0.15;

/** How far behind a wave's face ring the scatter still shows, in face bands: the point where
    the band's trailing edge, `exp(-z² · RIPPLE_BAND_TAIL)`, drops to `SCATTER_SEEN`. */
export const XRAY_TRAIL = Math.sqrt(-Math.log(SCATTER_SEEN) / RIPPLE_BAND_TAIL);

// The X-ray eases in over the first `FADE_IN` of the face's reach as the ring leaves the
// butterfly, and eases out as the visible scatter crosses the last `1 - FADE_OUT_FROM`.
const FADE_IN = 0.25;
const FADE_OUT_FROM = 0.6;

/** How strongly the X-ray shows for a wave's face ring, 0 to 1. `radius` and `thickness` are
    the ring's mean radius and band thickness in page pixels.

    The ring starts at the butterfly, which sits on the face, and its scatter trails
    `XRAY_TRAIL` face bands (`FACE_BAND_WIDEN` times the thickness) behind the radius. The X-ray
    eases in as the ring leaves the butterfly and out as that scatter passes the face's
    farthest dot, `reach` page pixels from the butterfly. */
export function xrayStrength(radius: number, thickness: number, reach: number): number {
  if (reach <= 0) return 0;
  const scatterEnd = radius - XRAY_TRAIL * FACE_BAND_WIDEN * thickness;
  const easeIn = smoothstep(0, FADE_IN * reach, radius);
  const easeOut = 1 - smoothstep(FADE_OUT_FROM * reach, reach, scatterEnd);
  return Math.min(easeIn, easeOut);
}

// The ring crosses the face in about a third of a second, so the X-ray also trails its
// strength a little, in engine seconds. It comes in fast, so the skeleton shows while the
// scatter opens up the face, and goes out slower, as the dots settle back.
const EASE_IN = 0.1;
const EASE_OUT = 0.16;

/** The X-ray's level after `dt` engine seconds, moving from `level` toward `target` (from
    `xrayStrength()`). It's an exponential lag, `EASE_IN` toward a stronger target and
    `EASE_OUT` toward a weaker one, and it never overshoots. */
export function easeXray(level: number, target: number, dt: number): number {
  const lag = target > level ? EASE_IN : EASE_OUT;
  return target + (level - target) * Math.exp(-Math.max(dt, 0) / lag);
}
